import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentArrowDownIcon, 
  ClipboardDocumentIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  LightBulbIcon,
  ArrowPathIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

// Token data (8 tokens from previous modules)
const tokenOptions = [
  { id: 'ETH', name: 'Ethereum', symbol: 'ETH' },
  { id: 'BTC', name: 'Bitcoin', symbol: 'BTC' },
  { id: 'SOL', name: 'Solana', symbol: 'SOL' },
  { id: 'AVAX', name: 'Avalanche', symbol: 'AVAX' },
  { id: 'MATIC', name: 'Polygon', symbol: 'MATIC' },
  { id: 'LINK', name: 'Chainlink', symbol: 'LINK' },
  { id: 'UNI', name: 'Uniswap', symbol: 'UNI' },
  { id: 'AAVE', name: 'Aave', symbol: 'AAVE' }
];

// Base metrics for each token (current state)
const baseTokenMetrics = {
  ETH: {
    spread: 0.12,
    marketDepth: 750000,
    slippage: 2.8,
    volumeCoverage: 85,
    tokenVelocity: 0.45
  },
  BTC: {
    spread: 0.08,
    marketDepth: 1200000,
    slippage: 1.5,
    volumeCoverage: 92,
    tokenVelocity: 0.32
  },
  SOL: {
    spread: 0.25,
    marketDepth: 420000,
    slippage: 4.2,
    volumeCoverage: 68,
    tokenVelocity: 0.78
  },
  AVAX: {
    spread: 0.18,
    marketDepth: 580000,
    slippage: 3.1,
    volumeCoverage: 72,
    tokenVelocity: 0.56
  },
  MATIC: {
    spread: 0.22,
    marketDepth: 340000,
    slippage: 3.8,
    volumeCoverage: 64,
    tokenVelocity: 0.82
  },
  LINK: {
    spread: 0.35,
    marketDepth: 280000,
    slippage: 4.5,
    volumeCoverage: 58,
    tokenVelocity: 0.41
  },
  UNI: {
    spread: 0.28,
    marketDepth: 390000,
    slippage: 3.6,
    volumeCoverage: 71,
    tokenVelocity: 0.63
  },
  AAVE: {
    spread: 0.42,
    marketDepth: 220000,
    slippage: 5.1,
    volumeCoverage: 52,
    tokenVelocity: 0.38
  }
};

// Calculation functions
const calculateLiquidityScore = (metrics) => {
  // Weighted scoring algorithm
  const spreadScore = Math.max(0, 100 - (metrics.spread / 0.5) * 100);
  const depthScore = Math.min(100, (metrics.marketDepth / 1000000) * 100);
  const slippageScore = Math.max(0, 100 - (metrics.slippage / 5) * 100);
  const volumeScore = metrics.volumeCoverage;
  const velocityScore = Math.min(100, (1 - Math.abs(metrics.tokenVelocity - 0.5)) * 100);

  const weightedScore = (
    spreadScore * 0.25 +
    depthScore * 0.25 +
    slippageScore * 0.2 +
    volumeScore * 0.2 +
    velocityScore * 0.1
  );

  return Math.round(Math.max(0, Math.min(100, weightedScore)));
};

const getRAGStatus = (score) => {
  if (score >= 80) return 'green';
  if (score >= 60) return 'amber';
  return 'red';
};

const getHealthClassification = (score) => {
  if (score >= 80) return 'Strong';
  if (score >= 60) return 'Watchlist';
  return 'Critical';
};

const generateInsight = (before, after, selectedToken) => {
  const scoreDiff = after.score - before.score;
  const ragChanged = before.rag !== after.rag;
  const healthChanged = before.health !== after.health;

  let insight = `For ${selectedToken}, `;

  if (scoreDiff > 10) {
    insight += `the simulation shows significant improvement with a ${scoreDiff.toFixed(1)} point increase in liquidity score. `;
  } else if (scoreDiff > 0) {
    insight += `the simulation shows modest improvement with a ${scoreDiff.toFixed(1)} point increase in liquidity score. `;
  } else if (scoreDiff < -5) {
    insight += `the simulation shows a concerning ${Math.abs(scoreDiff).toFixed(1)} point decrease in liquidity score. `;
  } else {
    insight += `the simulation shows minimal impact on the overall liquidity score. `;
  }

  if (healthChanged) {
    insight += `The health classification would change from ${before.health} to ${after.health}. `;
  }

  if (ragChanged) {
    insight += `The RAG status would improve from ${before.rag} to ${after.rag}. `;
  }

  // Add specific metric insights
  const improvements = [];
  const concerns = [];

  if (after.metrics.spread < before.metrics.spread) {
    improvements.push('tighter spreads');
  }
  if (after.metrics.marketDepth > before.metrics.marketDepth) {
    improvements.push('deeper market liquidity');
  }
  if (after.metrics.slippage < before.metrics.slippage) {
    improvements.push('reduced slippage');
  }
  if (after.metrics.volumeCoverage > before.metrics.volumeCoverage) {
    improvements.push('better volume coverage');
  }

  if (improvements.length > 0) {
    insight += `Key improvements include ${improvements.join(', ')}. `;
  }

  insight += `This simulation suggests ${scoreDiff > 5 ? 'strong potential' : scoreDiff > 0 ? 'moderate potential' : 'limited benefit'} for liquidity optimization.`;

  return insight;
};

// RAG Indicator Component
const RAGIndicator = ({ status, showArrow = true }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'green':
        return {
          bg: 'bg-green-500/20',
          border: 'border-green-500/50',
          text: 'text-green-500',
          arrow: '↑',
          label: 'Strong'
        };
      case 'amber':
        return {
          bg: 'bg-yellow-500/20',
          border: 'border-yellow-500/50',
          text: 'text-yellow-500',
          arrow: '→',
          label: 'Watchlist'
        };
      case 'red':
        return {
          bg: 'bg-red-500/20',
          border: 'border-red-500/50',
          text: 'text-red-500',
          arrow: '↓',
          label: 'Critical'
        };
      default:
        return {
          bg: 'bg-cream-100/20',
          border: 'border-cream-100/50',
          text: 'text-cream-100',
          arrow: '→',
          label: 'N/A'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.border} ${config.text}`}>
      {showArrow && <span className={`text-lg mr-2 ${config.text}`}>{config.arrow}</span>}
      {config.label}
    </span>
  );
};

// Metric Slider Component
const MetricSlider = ({ label, value, onChange, min, max, step, unit, description }) => {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-cream-100 font-medium text-sm">{label}</label>
        <span className="text-accent-blue font-mono text-sm">
          {typeof value === 'number' ? value.toFixed(step < 1 ? 2 : 0) : value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
        style={{
          background: `linear-gradient(to right, #4A90E2 0%, #4A90E2 ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.2) ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.2) 100%)`
        }}
      />
      <p className="text-cream-300 text-xs">{description}</p>
    </div>
  );
};

// Export functionality
const exportToCSV = (beforeData, afterData, selectedToken, fileName) => {
  const headers = ['Metric', 'Before', 'After', 'Change'];
  const csvContent = [
    headers.join(','),
    `"Token","${selectedToken}","${selectedToken}",""`,
    `"Liquidity Score","${beforeData.score}","${afterData.score}","${(afterData.score - beforeData.score).toFixed(1)}"`,
    `"RAG Status","${beforeData.rag}","${afterData.rag}","${beforeData.rag !== afterData.rag ? 'Changed' : 'No Change'}"`,
    `"Health Classification","${beforeData.health}","${afterData.health}","${beforeData.health !== afterData.health ? 'Changed' : 'No Change'}"`,
    `"Spread (%)","${beforeData.metrics.spread.toFixed(2)}","${afterData.metrics.spread.toFixed(2)}","${(afterData.metrics.spread - beforeData.metrics.spread).toFixed(2)}"`,
    `"Market Depth ($)","${beforeData.metrics.marketDepth}","${afterData.metrics.marketDepth}","${afterData.metrics.marketDepth - beforeData.metrics.marketDepth}"`,
    `"Slippage (%)","${beforeData.metrics.slippage.toFixed(2)}","${afterData.metrics.slippage.toFixed(2)}","${(afterData.metrics.slippage - beforeData.metrics.slippage).toFixed(2)}"`,
    `"Volume Coverage (%)","${beforeData.metrics.volumeCoverage}","${afterData.metrics.volumeCoverage}","${afterData.metrics.volumeCoverage - beforeData.metrics.volumeCoverage}"`,
    `"Token Velocity","${beforeData.metrics.tokenVelocity.toFixed(2)}","${afterData.metrics.tokenVelocity.toFixed(2)}","${(afterData.metrics.tokenVelocity - beforeData.metrics.tokenVelocity).toFixed(2)}"`
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToPDF = async (beforeData, afterData, selectedToken, insight, fileName) => {
  const printWindow = window.open('', '', 'height=900,width=1200');
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Valeon Simulation Strategy Report</title>
      <style>
        @page {
          size: A4;
          margin: 1in;
        }
        
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
          margin: 0;
          padding: 20px;
          color: #333;
          line-height: 1.6;
        }
        
        .header {
          text-align: center;
          margin-bottom: 40px;
          border-bottom: 3px solid #0B0E1C;
          padding-bottom: 20px;
        }
        
        .main-title {
          font-size: 24px;
          font-weight: bold;
          color: #0B0E1C;
          margin: 0 0 8px 0;
        }
        
        .token-name {
          font-size: 18px;
          color: #4A90E2;
          margin: 0;
        }
        
        .export-date {
          font-size: 12px;
          color: #888;
          margin-top: 8px;
        }
        
        .comparison-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin: 30px 0;
        }
        
        .metric-card {
          background: #f8f9fa;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
        }
        
        .card-title {
          font-size: 16px;
          font-weight: bold;
          color: #0B0E1C;
          margin-bottom: 15px;
          text-align: center;
        }
        
        .score-display {
          font-size: 32px;
          font-weight: bold;
          text-align: center;
          margin: 10px 0;
        }
        
        .score-green { color: #10B981; }
        .score-amber { color: #F59E0B; }
        .score-red { color: #EF4444; }
        
        .metrics-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        
        .metrics-table th, .metrics-table td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        
        .metrics-table th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        
        .insight-box {
          background: #e3f2fd;
          border-left: 4px solid #2196f3;
          padding: 20px;
          margin: 30px 0;
          border-radius: 4px;
        }
        
        .insight-title {
          font-size: 16px;
          font-weight: bold;
          color: #1976d2;
          margin-bottom: 10px;
        }
        
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 10px;
          color: #999;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="main-title">Valeon Simulation Strategy Report</h1>
        <p class="token-name">${selectedToken} Liquidity Optimization Analysis</p>
        <div class="export-date">Generated on: ${currentDate}</div>
      </div>
      
      <div class="comparison-section">
        <div class="metric-card">
          <h3 class="card-title">BEFORE Simulation</h3>
          <div class="score-display score-${beforeData.rag}">${beforeData.score}/100</div>
          <p style="text-align: center; margin: 5px 0;"><strong>RAG Status:</strong> ${beforeData.rag.toUpperCase()}</p>
          <p style="text-align: center; margin: 5px 0;"><strong>Health:</strong> ${beforeData.health}</p>
        </div>
        
        <div class="metric-card">
          <h3 class="card-title">AFTER Simulation</h3>
          <div class="score-display score-${afterData.rag}">${afterData.score}/100</div>
          <p style="text-align: center; margin: 5px 0;"><strong>RAG Status:</strong> ${afterData.rag.toUpperCase()}</p>
          <p style="text-align: center; margin: 5px 0;"><strong>Health:</strong> ${afterData.health}</p>
        </div>
      </div>
      
      <table class="metrics-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Before</th>
            <th>After</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Spread (%)</td>
            <td>${beforeData.metrics.spread.toFixed(2)}%</td>
            <td>${afterData.metrics.spread.toFixed(2)}%</td>
            <td>${(afterData.metrics.spread - beforeData.metrics.spread).toFixed(2)}%</td>
          </tr>
          <tr>
            <td>Market Depth ($)</td>
            <td>$${beforeData.metrics.marketDepth.toLocaleString()}</td>
            <td>$${afterData.metrics.marketDepth.toLocaleString()}</td>
            <td>$${(afterData.metrics.marketDepth - beforeData.metrics.marketDepth).toLocaleString()}</td>
          </tr>
          <tr>
            <td>Slippage (%)</td>
            <td>${beforeData.metrics.slippage.toFixed(2)}%</td>
            <td>${afterData.metrics.slippage.toFixed(2)}%</td>
            <td>${(afterData.metrics.slippage - beforeData.metrics.slippage).toFixed(2)}%</td>
          </tr>
          <tr>
            <td>Volume Coverage (%)</td>
            <td>${beforeData.metrics.volumeCoverage}%</td>
            <td>${afterData.metrics.volumeCoverage}%</td>
            <td>${afterData.metrics.volumeCoverage - beforeData.metrics.volumeCoverage}%</td>
          </tr>
          <tr>
            <td>Token Velocity</td>
            <td>${beforeData.metrics.tokenVelocity.toFixed(2)}</td>
            <td>${afterData.metrics.tokenVelocity.toFixed(2)}</td>
            <td>${(afterData.metrics.tokenVelocity - beforeData.metrics.tokenVelocity).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
      
      <div class="insight-box">
        <div class="insight-title">Strategy Insights</div>
        <p>${insight}</p>
      </div>
      
      <div class="footer">
        <p>Valeon Strategic Advisory | Simulation Strategy Analysis<br>
        This report provides strategic insights for ${selectedToken} liquidity optimization.</p>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

const SimulationStrategy = () => {
  const [selectedToken, setSelectedToken] = useState('ETH');
  const [simulatedMetrics, setSimulatedMetrics] = useState({
    spread: baseTokenMetrics.ETH.spread,
    marketDepth: baseTokenMetrics.ETH.marketDepth,
    slippage: baseTokenMetrics.ETH.slippage,
    volumeCoverage: baseTokenMetrics.ETH.volumeCoverage,
    tokenVelocity: baseTokenMetrics.ETH.tokenVelocity
  });

  // Calculate current and simulated scores
  const currentMetrics = baseTokenMetrics[selectedToken];
  const currentScore = useMemo(() => calculateLiquidityScore(currentMetrics), [selectedToken]);
  const simulatedScore = useMemo(() => calculateLiquidityScore(simulatedMetrics), [simulatedMetrics]);

  const beforeData = {
    score: currentScore,
    rag: getRAGStatus(currentScore),
    health: getHealthClassification(currentScore),
    metrics: currentMetrics
  };

  const afterData = {
    score: simulatedScore,
    rag: getRAGStatus(simulatedScore),
    health: getHealthClassification(simulatedScore),
    metrics: simulatedMetrics
  };

  const insight = useMemo(() => generateInsight(beforeData, afterData, selectedToken), [beforeData, afterData, selectedToken]);

  // Handle token change
  const handleTokenChange = (tokenId) => {
    setSelectedToken(tokenId);
    setSimulatedMetrics({ ...baseTokenMetrics[tokenId] });
  };

  // Reset to current values
  const handleReset = () => {
    setSimulatedMetrics({ ...baseTokenMetrics[selectedToken] });
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-card p-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-2">
              Simulation & Strategy
            </h2>
            <p className="text-cream-200">
              Interactive modeling tools for strategic liquidity optimization scenarios.
            </p>
          </div>
          
          {/* Export Buttons */}
          <div className="flex space-x-3 mt-4 lg:mt-0">
            <button
              onClick={() => exportToCSV(beforeData, afterData, selectedToken, 'Simulation_Strategy_Export')}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-blue/20 border border-accent-blue/30 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-all duration-200"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              <span className="text-sm font-medium">CSV</span>
            </button>
            <button
              onClick={() => exportToPDF(beforeData, afterData, selectedToken, insight, 'Simulation_Strategy_Export')}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-gold/20 border border-accent-gold/30 text-accent-gold rounded-lg hover:bg-accent-gold/30 transition-all duration-200"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              <span className="text-sm font-medium">PDF</span>
            </button>
          </div>
        </div>

        {/* Token Selector */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8 p-4 bg-white/5 rounded-xl border border-white/10"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-5 w-5 text-cream-300" />
              <span className="text-cream-200 font-medium text-sm">Select Token for Simulation:</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <select
                  value={selectedToken}
                  onChange={(e) => handleTokenChange(e.target.value)}
                  className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
                >
                  {tokenOptions.map(token => (
                    <option key={token.id} value={token.id} className="bg-primary-800 text-cream-100">
                      {token.name} ({token.symbol})
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
              </div>
              <button
                onClick={handleReset}
                className="flex items-center space-x-2 px-3 py-2 bg-white/10 border border-white/20 text-cream-200 rounded-lg hover:bg-white/15 transition-all duration-200"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Before/After Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
        >
          {/* Current (Before) */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-4 text-center">
              Current State
            </h3>
            <div className="text-center mb-4">
              <div className={`text-5xl font-bold mb-2 ${getRAGStatus(currentScore) === 'green' ? 'text-green-500' : getRAGStatus(currentScore) === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
                {currentScore}
              </div>
              <div className="text-cream-300 text-sm mb-3">Liquidity Score</div>
              <RAGIndicator status={getRAGStatus(currentScore)} />
              <div className="mt-2 text-cream-200 font-medium">
                {getHealthClassification(currentScore)}
              </div>
            </div>
          </div>

          {/* Simulated (After) */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-4 text-center">
              Simulated State
            </h3>
            <div className="text-center mb-4">
              <div className={`text-5xl font-bold mb-2 ${getRAGStatus(simulatedScore) === 'green' ? 'text-green-500' : getRAGStatus(simulatedScore) === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
                {simulatedScore}
              </div>
              <div className="text-cream-300 text-sm mb-3">Liquidity Score</div>
              <RAGIndicator status={getRAGStatus(simulatedScore)} />
              <div className="mt-2 text-cream-200 font-medium">
                {getHealthClassification(simulatedScore)}
              </div>
              <div className="mt-3 text-center">
                <span className={`text-sm font-medium ${simulatedScore > currentScore ? 'text-green-500' : simulatedScore < currentScore ? 'text-red-500' : 'text-cream-300'}`}>
                  {simulatedScore > currentScore ? '+' : ''}{(simulatedScore - currentScore).toFixed(1)} points
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Simulation Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10 mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-accent-blue" />
            <h3 className="text-heading-md font-serif font-bold text-cream-100">
              Simulation Controls
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MetricSlider
              label="Spread"
              value={simulatedMetrics.spread}
              onChange={(value) => setSimulatedMetrics(prev => ({ ...prev, spread: value }))}
              min={0.01}
              max={1.0}
              step={0.01}
              unit="%"
              description="Bid-ask spread - lower values improve liquidity score"
            />

            <MetricSlider
              label="Market Depth"
              value={simulatedMetrics.marketDepth}
              onChange={(value) => setSimulatedMetrics(prev => ({ ...prev, marketDepth: value }))}
              min={50000}
              max={2000000}
              step={10000}
              unit="$"
              description="Available liquidity at 1% price impact"
            />

            <MetricSlider
              label="Slippage"
              value={simulatedMetrics.slippage}
              onChange={(value) => setSimulatedMetrics(prev => ({ ...prev, slippage: value }))}
              min={0.5}
              max={10.0}
              step={0.1}
              unit="%"
              description="Price impact for $100k trade size"
            />

            <MetricSlider
              label="Volume Coverage"
              value={simulatedMetrics.volumeCoverage}
              onChange={(value) => setSimulatedMetrics(prev => ({ ...prev, volumeCoverage: value }))}
              min={20}
              max={100}
              step={1}
              unit="%"
              description="Percentage of total volume captured"
            />

            <MetricSlider
              label="Token Velocity"
              value={simulatedMetrics.tokenVelocity}
              onChange={(value) => setSimulatedMetrics(prev => ({ ...prev, tokenVelocity: value }))}
              min={0.1}
              max={1.5}
              step={0.01}
              unit=""
              description="On-chain usage velocity (optimal: 0.3-0.6)"
            />
          </div>
        </motion.div>

        {/* Insight Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-gradient-to-r from-accent-blue/10 to-accent-gold/10 rounded-xl p-6 border border-accent-blue/20"
        >
          <div className="flex items-center space-x-2 mb-4">
            <LightBulbIcon className="h-5 w-5 text-accent-blue" />
            <h3 className="text-heading-md font-serif font-bold text-cream-100">
              Strategy Insights
            </h3>
          </div>
          <p className="text-cream-200 leading-relaxed">
            {insight}
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SimulationStrategy;